import {api} from '@/services/http/api'
import {BaseResponse} from '@/interfaces'
import {
  UserCreateParams,
  LoginResponse,
  UserCreateResponse,
} from '@/services/http/user/models/User'
import {LogLevel} from '@/config/telemetry'
import {logTelemetry} from '@/telemetry'

const UserService = {
  login: async (email: string, password: string) => {
    interface ErrorResponse {
      response?: {
        data: {
          Message: string
        }
      }
    }

    try {
      logTelemetry(LogLevel.INFO, [
        `Iniciando requisição de login para ${email}`,
      ])

      const response = await api.post<BaseResponse<LoginResponse>>(
        '/v1/login',
        undefined,
        {
          headers: {
            email,
            password,
          },
        },
      )

      logTelemetry(LogLevel.INFO, [`Login bem-sucedido para ${email}`])

      return response.data
    } catch (error: unknown) {
      const e = error as ErrorResponse

      if (e.response?.data.Message) {
        const errorMessage: string = e.response.data.Message

        if (errorMessage === 'Usuário e/ou senha inválidos.') {
          logTelemetry(LogLevel.WARN, [
            `Erro ao realizar login para ${email}, erro de `,
            errorMessage,
          ])
        } else {
          logTelemetry(LogLevel.ERROR, [
            `Erro ao realizar login para ${email}`,
            errorMessage,
          ])
        }
      }
      throw error
    }
  },
  isResetPsswdTokenValid: async (token: string) => {
    try {
      logTelemetry(LogLevel.INFO, [
        `Iniciando verificação de validade hash de reset ${token}`,
      ])

      const response = await api.post<BaseResponse<{valid: boolean}>>(
        '/v1/redefinir-senha/validar',
        undefined,
        {
          headers: {
            tokenHash: token,
          },
        },
      )

      const isValid = response.data.Data.valid
      logTelemetry(LogLevel.INFO, [`Hash ${token} verificada com sucesso`])

      return isValid
    } catch (error: unknown) {
      if (error instanceof Error) {
        logTelemetry(LogLevel.ERROR, [
          `Erro ao verificar hash ${token}`,
          error.message,
        ])
      } else {
        logTelemetry(LogLevel.ERROR, [`Erro desconhecido para ${token}`])
      }

      throw error
    }
  },
  isCreationPsswdTokenValid: async (token: string) => {
    try {
      logTelemetry(LogLevel.INFO, [
        `Iniciando verificação de validade hash de criação ${token}`,
      ])

      const response = await api.post<BaseResponse<{valid: boolean}>>(
        '/v1/usuario/validar-token',
        undefined,
        {
          headers: {
            tokenHash: token,
          },
        },
      )

      const isValid = response.data.Data.valid
      logTelemetry(LogLevel.INFO, [`Hash ${token} verificada com sucesso`])

      return isValid
    } catch (error: unknown) {
      if (error instanceof Error) {
        logTelemetry(LogLevel.ERROR, [
          `Erro ao verificar hash ${token}`,
          error.message,
        ])
      } else {
        logTelemetry(LogLevel.ERROR, [`Erro desconhecido para ${token}`])
      }

      throw error
    }
  },
  requestNewPassword: async (email: string) => {
    try {
      logTelemetry(LogLevel.INFO, [
        `Iniciando a solicitação de nova senha para o e-mail ${email}`,
      ])

      const response = await api.post<BaseResponse<null>>(
        '/v1/redefinir-senha/solicitar',
        undefined,
        {
          headers: {
            email,
          },
        },
      )

      logTelemetry(LogLevel.INFO, [
        `Operação realizada com sucesso para o e-mail ${email}`,
      ])

      return response.data
    } catch (error: unknown) {
      if (error instanceof Error) {
        logTelemetry(LogLevel.ERROR, [
          `Erro ao solicitar reset de senha para o email ${email}: ${error.message}`,
        ])
      } else {
        logTelemetry(LogLevel.ERROR, [
          `Erro desconhecido ao solicitar reset de senha para o email: ${email}`,
        ])
      }
      throw error
    }
  },
  resetPassword: async (hash: string, password: string) => {
    try {
      logTelemetry(LogLevel.INFO, [
        `Iniciando Reset de Senha para a hash ${hash}`,
      ])

      const response = await api.post<BaseResponse<LoginResponse>>(
        '/v1/redefinir-senha/alterar',
        undefined,
        {
          headers: {
            tokenHash: hash,
            newPassword: password,
          },
        },
      )

      logTelemetry(LogLevel.INFO, [
        `Operação de reset de senha realizada com sucesso para a hash ${hash}`,
      ])

      return response.data
    } catch (error: unknown) {
      if (error instanceof Error) {
        logTelemetry(LogLevel.ERROR, [
          `Erro ao resetar o password para a hash ${hash}: ${error.message}`,
        ])
      } else {
        logTelemetry(LogLevel.ERROR, [
          `Erro desconhecido no processo de reset de senha para a hash ${hash}`,
        ])
      }
      throw error
    }
  },
  create: async (user: UserCreateParams) => {
    const response = await api.post<UserCreateResponse>(
      '/v1/usuario/criar',
      user,
    )
    return response.data
  },
  activate: async (tokenHash: string, password: string) => {
    const response = await api.post<UserCreateResponse>(
      '/v1/usuario/ativar',
      undefined,
      {
        headers: {
          tokenHash,
          password,
        },
      },
    )
    return response.data
  },
}

export default UserService
